import { Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { copyTextToClipboard } from "@/lib/clipboard";

/**
 * The one "Copy value" tooltip a table body shares between all of its copy
 * buttons, so a page of rows doesn't mount a tooltip root per cell.
 */
export function BaseTableV3CellCopyTooltip({ handle }) {
  // A hoverable tooltip takes pointer events while open, and it opens over the
  // copy button of the cell above, where it would swallow that button's click.
  return (
    <Tooltip handle={handle} disableHoverablePopup>
      <TooltipContent>Copy value</TooltipContent>
    </Tooltip>
  );
}

/**
 * Copy control for a table cell, revealed on hover or keyboard focus. It relies
 * on the cell being a `tw:group tw:relative` container that carries
 * `data-empty` when it has no text to copy.
 */
export function BaseTableV3CellCopyButton({ getText, tooltipHandle }) {
  const handleClick = event => {
    // The cell may sit inside a link or a clickable row; copying must not navigate.
    event.stopPropagation();
    event.preventDefault();
    copyTextToClipboard(getText());
  };

  // The button is centered with margins, not translate-y: Button's 1px press
  // effect overwrites the same translate variable, so a translate-centered
  // button jumps on mousedown and a press on its upper half never clicks.
  // my-auto needs `!` because Semantic UI's unlayered reset zeroes button
  // margins, and unlayered CSS beats Tailwind's layered utilities.
  return (
    <TooltipTrigger
      handle={tooltipHandle}
      render={
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Copy value"
          onClick={handleClick}
          className="tw:absolute tw:inset-y-0 tw:right-1 tw:my-auto! tw:bg-[var(--color-surface)] tw:opacity-0 tw:group-hover:opacity-100 tw:focus-visible:opacity-100 tw:group-data-[empty]:hidden"
        >
          <Copy />
        </Button>
      }
    />
  );
}
